"use client"

import { useState, useEffect } from "react"
import axios from "../api/axios"
import { toast } from "react-toastify"
import {
  Package,
  Plus,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Truck,
  User,
  MessageSquare,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Factory,
  Building2,
  Layers,
} from "lucide-react"

function ContainerStatus() {
  const [user, setUser] = useState(null)
  const [containers, setContainers] = useState([])
  const [form, setForm] = useState({
    name: "",
    containerNo: "",
    expectedDate: "",
    statusId: "",
  })
  const [editId, setEditId] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [pfiMonthFilter, setPfiMonthFilter] = useState("all")
  const [exFactoryMonthFilter, setExFactoryMonthFilter] = useState("all")
  const [selectedContainer, setSelectedContainer] = useState(null)
  const [selectedContainerexfactory, setSelectedContainerexfactory] = useState(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showStatusModalExFactory, setShowStatusModalExFactory] = useState(false)
  const [delayReason, setDelayReason] = useState("")
  const [delayReasonExFactory, setDelayReasonExFactory] = useState("")
  const [expandedLogs, setExpandedLogs] = useState({})
  const [expandedExFactoryLogs, setExpandedExFactoryLogs] = useState({})

  const [newContainer, setNewContainer] = useState({
    name: "",
    containerNo: "",
    expectedDate: "",
    exFactoryDate: "",
  })

  const fetchContainers = async () => {
    try {
      await axios.get("/sanctum/csrf-cookie")

      const userRes = await axios.get("/api/user", { withCredentials: true })
      setUser(userRes.data)

      const res = await axios.get("/api/containers")
      setContainers(res.data)
    } catch (error) {
      console.error("Failed to fetch containers:", error)
      toast.error("Error loading containers")
    }
  }

  useEffect(() => {
    fetchContainers()
  }, [])

  const handleSaveContainer = async () => {
    try {
      await axios.get("/sanctum/csrf-cookie")

      if (editId) {
        await axios.put(`/api/containers/${editId}`, {
          name: form.name,
          container_no: form.containerNo,
          expected_date: form.expectedDate,
          ex_factory_date: form.exFactoryDate,
          status_id: form.statusId,
        })
        toast.success("Container updated successfully!")
      } else {
        await axios.post("/api/containers", {
          name: form.name,
          container_no: form.containerNo,
          expected_date: form.expectedDate,
          ex_factory_date: form.exFactoryDate,
          status_id: form.statusId,
        })
        toast.success("Container created successfully!")
      }

      setForm({
        name: "",
        containerNo: "",
        expectedDate: "",
        exFactoryDate: "",
        statusId: "",
      })
      setEditId(null)
      setShowCreateForm(false)
      fetchContainers()
    } catch (error) {
      console.error("Error saving container:", error)
      toast.error("Failed to save container")
    }
  }

  useEffect(() => {
    const checkDelayedContainers = () => {
      const today = new Date()
      setContainers((prev) =>
        prev.map((container) => {
          if (container.status_name === "Not Received") {
            const expectedDate = new Date(container.expectedDate)
            if (today > expectedDate) {
              return {
                ...container,
                status: "Delayed",
                delayReason: "Expected date crossed",
              }
            }
          }
          return container
        }),
      )
    }

    checkDelayedContainers()
    const interval = setInterval(checkDelayedContainers, 60000)
    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case "Not Received":
        return "bg-amber-50 text-amber-700 border-amber-200"
      case "Received":
        return "bg-blue-50 text-blue-700 border-blue-200"
      case "Departed":
        return "bg-emerald-50 text-emerald-700 border-emerald-200"
      case "Delayed":
        return "bg-red-50 text-red-700 border-red-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "Not Received":
        return <Clock size={16} />
      case "Received":
        return <CheckCircle size={16} />
      case "Departed":
        return <Truck size={16} />
      case "Delayed":
        return <AlertTriangle size={16} />
      default:
        return <Clock size={16} />
    }
  }

  const getEffectiveStatus = (container) => {
    if (container.status_name === "Not Received" && new Date(container.expected_date) < new Date()) {
      return "Delayed"
    }
    return container.status_name
  }

  const handleStatusChange = async (container, newStatus) => {
    setSelectedContainer(container)
    setSelectedContainerexfactory(container)

    if (newStatus === "Delayed") {
      setShowStatusModal(true)
      return
    }
    if (newStatus === "Ex factory delayed") {
      setShowStatusModalExFactory(true)
      return
    }

    await updateContainerStatus(container.id, newStatus)
  }

  const updateContainerStatus = async (id, newStatus, reason = null) => {
    try {
      await axios.get("/sanctum/csrf-cookie")

      const response = await axios.put(`/api/containers/${id}/status`, {
        status: newStatus,
        delay_reason: reason,
      })

      const updated = response.data

      setContainers((prev) => prev.map((c) => (c.id === id ? updated : c)))

      setShowStatusModal(false)
      setShowStatusModalExFactory(false)
      setDelayReason("")
      setDelayReasonExFactory("")
      setSelectedContainer(null)
      setSelectedContainerexfactory(null)

      await fetchContainers()

      toast.success("Container updated successfully!")
    } catch (err) {
      console.error("Failed to update status", err)
      toast.error("Failed to update Container")
    }
  }

  const handleDeleteContainer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this container?")) return

    try {
      await axios.get("/sanctum/csrf-cookie")
      await axios.delete(`/api/containers/${id}`)
      toast.success("Container deleted successfully!")
      fetchContainers()
    } catch (error) {
      console.error("Error deleting container:", error)
      toast.error("Failed to delete container")
    }
  }

  const handleDelaySubmit = () => {
    const reason = delayReason.trim() || delayReasonExFactory.trim()
    const containerId = selectedContainer?.id || selectedContainerexfactory?.id

    if (!reason) {
      toast.error("Please provide a delay reason")
      return
    }

    if (!containerId) {
      toast.error("No container selected")
      return
    }

    if (delayReason.trim() !== "") {
      updateContainerStatus(containerId, "Delayed", delayReason.trim())
    } else if (delayReasonExFactory.trim() !== "") {
      updateContainerStatus(containerId, "Ex Factory Delayed", delayReasonExFactory.trim())
    }
  }

  const [delayLogsMap, setDelayLogsMap] = useState({})

  const fetchDelayLogs = async (containerId) => {
    try {
      const response = await axios.get(`/api/containers/${containerId}/delay-logs`)
      setDelayLogsMap((prev) => ({
        ...prev,
        [containerId]: response.data,
      }))
    } catch (error) {
      console.error("Failed to fetch delay logs", error)
    }
  }

  useEffect(() => {
    containers.forEach((container) => {
      fetchDelayLogs(container.id)
    })
  }, [containers])

  const [exfactorydelayLogsMap, setExFactoryDelayLogsMap] = useState({})

  const fetchExFctoryDelayLogs = async (containerId) => {
    try {
      const response = await axios.get(`/api/containers/${containerId}/ex-factory-delay-logs`)
      setExFactoryDelayLogsMap((prev) => ({
        ...prev,
        [containerId]: response.data,
      }))
    } catch (error) {
      console.error("Failed to fetch delay logs", error)
    }
  }

  useEffect(() => {
    containers.forEach((container) => {
      fetchExFctoryDelayLogs(container.id)
    })
  }, [containers])

  const toggleLogExpansion = (containerId, logType) => {
    if (logType === "pfi") {
      setExpandedLogs((prev) => ({
        ...prev,
        [containerId]: !prev[containerId],
      }))
    } else {
      setExpandedExFactoryLogs((prev) => ({
        ...prev,
        [containerId]: !prev[containerId],
      }))
    }
  }

  const filteredContainers = containers.filter((container) => {
    if (!container) return false

    const name = container.name?.toLowerCase() || ""
    const containerNo = container.container_no?.toLowerCase() || ""

    const matchesSearch = name.includes(searchTerm.toLowerCase()) || containerNo.includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "Delayed" ? container.is_delayed_pfi === 1 : container.status_name === statusFilter)

    // PFI Month Filter
    const matchesPfiMonth =
      pfiMonthFilter === "all" ||
      (container.pfi_date && new Date(container.pfi_date).getMonth() + 1 === Number.parseInt(pfiMonthFilter))

    // Ex-Factory Month Filter
    const matchesExFactoryMonth =
      exFactoryMonthFilter === "all" ||
      (container.ex_factory_date &&
        new Date(container.ex_factory_date).getMonth() + 1 === Number.parseInt(exFactoryMonthFilter))

    return matchesSearch && matchesStatus && matchesPfiMonth && matchesExFactoryMonth
  })

  const statusCounts = {
    all: containers.length,
    "Not Received": containers.filter((c) => c.status_name === "Not Received").length,
    Received: containers.filter((c) => c.status_name === "Received").length,
    Departed: containers.filter((c) => c.status_name === "Departed").length,
    Delayed: containers.filter((c) => c.is_delayed_pfi === 1).length,
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl shadow-lg">
              <Package size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Container Status Management</h1>
              <p className="text-lg text-gray-600 mt-1">
                Track and manage container deliveries with comprehensive status updates
              </p>
            </div>
          </div>

          {/* Enhanced Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Layers size={24} className="text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">All</h3>
                  <p className="text-2xl font-bold text-gray-900">{statusCounts.all}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-amber-100 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Clock size={24} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-900">Not Received</h3>
                  <p className="text-2xl font-bold text-amber-900">{statusCounts["Not Received"]}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-blue-100 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <CheckCircle size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">Received</h3>
                  <p className="text-2xl font-bold text-blue-900">{statusCounts["Received"]}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-emerald-100 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Truck size={24} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-900">Departed</h3>
                  <p className="text-2xl font-bold text-emerald-900">{statusCounts["Departed"]}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-red-100 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-red-100 rounded-xl">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-900">Delayed</h3>
                  <p className="text-2xl font-bold text-red-900">{statusCounts["Delayed"]}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Search and Filters */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex flex-col space-y-4">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search containers by name or number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                  />
                </div>
              </div>

              {/* Filter Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Filter size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="Not Received">Not Received</option>
                    <option value="Received">Received</option>
                    <option value="Departed">Departed</option>
                    <option value="Delayed">Delayed</option>
                  </select>
                </div>

                <div className="relative">
                  <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={pfiMonthFilter}
                    onChange={(e) => setPfiMonthFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">PFI Date - All Months</option>
                    {monthNames.map((month, index) => (
                      <option key={index + 1} value={index + 1}>
                        PFI - {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <Factory size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={exFactoryMonthFilter}
                    onChange={(e) => setExFactoryMonthFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">Ex-Factory - All Months</option>
                    {monthNames.map((month, index) => (
                      <option key={index + 1} value={index + 1}>
                        Ex-Factory - {month}
                      </option>
                    ))}
                  </select>
                </div>

                {(user?.role_id === 1 || user?.role_id === 2) && (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <Plus size={20} />
                    <span>Add Container</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Create Container Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Package size={24} className="mr-3 text-orange-600" />
                {editId ? "Edit Container" : "Add New Container"}
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Container Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter container name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Order Number</label>
                  <input
                    type="text"
                    value={form.containerNo}
                    onChange={(e) => setForm({ ...form, containerNo: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter container number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PFI Date</label>
                  <input
                    type="date"
                    value={form.expectedDate || ""}
                    onChange={(e) => setForm({ ...form, expectedDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ex-Factory Date</label>
                  <input
                    type="date"
                    value={form.exFactoryDate || ""}
                    onChange={(e) => setForm({ ...form, exFactoryDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowCreateForm(false)
                    setForm({
                      name: "",
                      containerNo: "",
                      expectedDate: "",
                      exFactoryDate: "",
                      statusId: "",
                    })
                    setEditId(null)
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveContainer}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-colors font-semibold"
                >
                  {editId ? "Update Container" : "Create Container"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PFI Delay Reason Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <AlertTriangle size={24} className="mr-3 text-red-600" />
                Mark as PFI Delayed
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for PFI Delay</label>
                <textarea
                  value={delayReason}
                  onChange={(e) => setDelayReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={4}
                  placeholder="Enter detailed reason for PFI delay..."
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false)
                    setDelayReason("")
                    setSelectedContainer(null)
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelaySubmit}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                >
                  Mark as PFI Delayed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ex Factory Delay Reason Modal */}
        {showStatusModalExFactory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Factory size={24} className="mr-3 text-red-600" />
                Mark as Ex-Factory Delayed
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Ex-Factory Delay</label>
                <textarea
                  value={delayReasonExFactory}
                  onChange={(e) => setDelayReasonExFactory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={4}
                  placeholder="Enter detailed reason for ex-factory delay..."
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModalExFactory(false)
                    setDelayReasonExFactory("")
                    setSelectedContainerexfactory(null)
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelaySubmit}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                >
                  Mark as Ex-Factory Delayed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Containers List */}
        <div className="space-y-6">
          {filteredContainers.map((container) => (
            <div
              key={container.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="p-8">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-6 lg:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg">
                        <Package size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{container.name}</h3>
                        <p className="text-lg text-gray-600 flex items-center mt-1">
                          <Building2 size={16} className="mr-2" />
                          Order No: {container.container_no}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-6">
                      <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-xl">
                        <Calendar size={18} className="text-blue-600" />
                        <div>
                          <p className="font-semibold text-blue-900">PFI Date</p>
                          <p className="text-blue-700">{new Date(container.pfi_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-xl">
                        <Factory size={18} className="text-purple-600" />
                        <div>
                          <p className="font-semibold text-purple-900">Ex-Factory Date</p>
                          <p className="text-purple-700">{new Date(container.ex_factory_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                        <User size={18} className="text-green-600" />
                        <div>
                          <p className="font-semibold text-green-900">Created by</p>
                          <p className="text-green-700">{container.user_name}</p>
                        </div>
                      </div>
                      {container.checkedBy && (
                        <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-xl">
                          <CheckCircle size={18} className="text-amber-600" />
                          <div>
                            <p className="font-semibold text-amber-900">Checked by</p>
                            <p className="text-amber-700">{container.checkedBy}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* PFI Delay Logs - Collapsible */}
                    {(container.is_delayed_pfi === 1 ||
                      (new Date(container.pfi_date) <= new Date(new Date().toDateString()) &&
                        (new Date(container.pfi_date).toDateString() !== new Date().toDateString() ||
                          new Date().getHours() >= 18))) && (
                      <div className="mb-4">
                        <button
                          onClick={() => toggleLogExpansion(container.id, "pfi")}
                          className="w-full flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <AlertTriangle size={20} className="text-red-600" />
                            <div className="text-left">
                              <p className="font-semibold text-red-900">PFI Delay History</p>
                              <p className="text-sm text-red-700">
                                {delayLogsMap[container.id]?.length || 0} delay entries
                              </p>
                            </div>
                          </div>
                          {expandedLogs[container.id] ? (
                            <ChevronUp size={20} className="text-red-600" />
                          ) : (
                            <ChevronDown size={20} className="text-red-600" />
                          )}
                        </button>

                        {expandedLogs[container.id] && (
                          <div className="mt-3 p-4 bg-red-25 border border-red-100 rounded-xl">
                            <div className="space-y-3">
                              {delayLogsMap[container.id]?.length > 0 ? (
                                delayLogsMap[container.id].map((log, index) => (
                                  <div key={index} className="p-3 bg-white rounded-lg border border-red-100">
                                    <div className="flex items-start space-x-3">
                                      <MessageSquare size={16} className="text-red-600 mt-1" />
                                      <div className="flex-1">
                                        <p className="font-semibold text-red-900">{log.user_name}</p>
                                        <p className="text-red-800 mt-1">{log.reason}</p>
                                        <p className="text-xs text-red-600 mt-2">
                                          {new Date(log.logged_at).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-red-600 text-center py-4">No delay reasons logged yet.</p>
                              )}
                            </div>
                            {/* {(user?.role_id === 1 || user?.role_id === 2) && (
                              <button
                                onClick={() => {
                                  setSelectedContainer(container)
                                  setShowStatusModal(true)
                                }}
                                className="mt-4 w-full px-4 py-2 bg-red-100 text-red-800 font-semibold rounded-lg border border-red-300 hover:bg-red-200 transition-colors"
                              >
                                Add Delay Reason
                              </button>
                            )} */}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Ex-Factory Delay Logs - Collapsible */}
                    {(container.is_delayed_ex_factory === 1 ||
                      (new Date(container.pfi_date) <= new Date(new Date().toDateString()) &&
                        (new Date(container.pfi_date).toDateString() !== new Date().toDateString() ||
                          new Date().getHours() >= 18))) && (
                      <div className="mb-4">
                        <button
                          onClick={() => toggleLogExpansion(container.id, "exfactory")}
                          className="w-full flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <Factory size={20} className="text-orange-600" />
                            <div className="text-left">
                              <p className="font-semibold text-orange-900">Ex-Factory Delay History</p>
                              <p className="text-sm text-orange-700">
                                {exfactorydelayLogsMap[container.id]?.length || 0} delay entries
                              </p>
                            </div>
                          </div>
                          {expandedExFactoryLogs[container.id] ? (
                            <ChevronUp size={20} className="text-orange-600" />
                          ) : (
                            <ChevronDown size={20} className="text-orange-600" />
                          )}
                        </button>

                        {expandedExFactoryLogs[container.id] && (
                          <div className="mt-3 p-4 bg-orange-25 border border-orange-100 rounded-xl">
                            <div className="space-y-3">
                              {exfactorydelayLogsMap[container.id]?.length > 0 ? (
                                exfactorydelayLogsMap[container.id].map((log, index) => (
                                  <div key={index} className="p-3 bg-white rounded-lg border border-orange-100">
                                    <div className="flex items-start space-x-3">
                                      <MessageSquare size={16} className="text-orange-600 mt-1" />
                                      <div className="flex-1">
                                        <p className="font-semibold text-orange-900">{log.user_name}</p>
                                        <p className="text-orange-800 mt-1">{log.reason}</p>
                                        <p className="text-xs text-orange-600 mt-2">
                                          {new Date(log.logged_at).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-orange-600 text-center py-4">No delay reasons logged yet.</p>
                              )}
                            </div>
                            {/* {(user?.role_id === 1 || user?.role_id === 2) && (
                              <button
                                onClick={() => {
                                  setSelectedContainerexfactory(container)
                                  setShowStatusModalExFactory(true)
                                }}
                                className="mt-4 w-full px-4 py-2 bg-orange-100 text-orange-800 font-semibold rounded-lg border border-orange-300 hover:bg-orange-200 transition-colors"
                              >
                                Add Delay Reason
                              </button>
                            )} */}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end space-y-4 lg:ml-8">
                    <div
                      className={`px-6 py-3 rounded-xl border-2 font-semibold text-lg flex items-center space-x-3 ${getStatusColor(
                        getEffectiveStatus(container),
                      )}`}
                    >
                      {(container.is_delayed_pfi === 1 || container.is_delayed_ex_factory === 1) && (
                        <div className="flex items-center space-x-2">
                          <AlertTriangle size={18} className="text-red-600" />
                          <p className="text-red-600 font-bold">Delayed</p>
                        </div>
                      )}

                      {getStatusIcon(getEffectiveStatus(container))}
                      <span className="capitalize">{getEffectiveStatus(container).replace("_", " ")}</span>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                      {container.status_name === "Not Received" && (
                        <button
                          onClick={() => handleStatusChange(container, "Received")}
                          disabled={user?.role_id === 3}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                            user?.role_id === 3
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300"
                          }`}
                        >
                          Mark Received
                        </button>
                      )}

                      {container.status_name === "Received" && (
                        <button
                          onClick={() => handleStatusChange(container, "Departed")}
                          disabled={user?.role_id === 3}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                            user?.role_id === 3
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700 border border-emerald-300"
                          }`}
                        >
                          Mark Departed
                        </button>
                      )}

                      {container.status_name === "Delayed" && (
                        <button
                          onClick={() => handleStatusChange(container, "Received")}
                          disabled={user?.role_id === 3}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                            user?.role_id === 3
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300"
                          }`}
                        >
                          Mark Received
                        </button>
                      )}

                      {container.status_name === "Departed" && (
                        <button
                          onClick={() => handleStatusChange(container, "Received")}
                          disabled={user?.role_id === 3}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                            user?.role_id === 3
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-300"
                          }`}
                        >
                          Undo Departed
                        </button>
                      )}

                      {(container.status_name === "Not Received" || container.status_name === "Received") && (
                        <>
                          <button
                            // onClick={() => handleStatusChange(container, "delayed")}
                             onClick={() => {
                                  setSelectedContainer(container)
                                  setShowStatusModal(true)
                                }}
                            disabled={user?.role_id === 3}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                              user?.role_id === 3
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
                            }`}
                          >
                            Delay PFI
                          </button>
                          <button
                            // onClick={() => handleStatusChange(container, "Ex Factory delayed")}
                            onClick={() => {
                                  setSelectedContainerexfactory(container)
                                  setShowStatusModalExFactory(true)
                                }}
                            disabled={user?.role_id === 3}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                              user?.role_id === 3
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300"
                            }`}
                          >
                            Delay Ex-Factory
                          </button>
                        </>
                      )}

                      {(user?.role_id === 1 || user?.role_id === 2) && (
                        <button
                          onClick={() => handleDeleteContainer(container.id)}
                          className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-semibold transition-all duration-200 border border-red-300"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredContainers.length === 0 && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
              <Package size={64} className="mx-auto text-gray-300 mb-6" />
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">No containers found</h3>
              <p className="text-gray-500 text-lg">
                {searchTerm || statusFilter !== "all" || pfiMonthFilter !== "all" || exFactoryMonthFilter !== "all"
                  ? "Try adjusting your search criteria or filters"
                  : "Create your first container to get started"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContainerStatus
